import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const ProvenancePane = ({ url, onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mock C2PA and provenance analysis
  useEffect(() => {
    if (url) {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        setAnalysis({
          url: url,
          domain: new URL(url)?.hostname,
          title: "Sample Article Title from Pasted URL",
          publishDate: "2024-09-20",
          author: "John Smith",
          c2paManifest: {
            present: Math.random() > 0.5,
            verified: Math.random() > 0.3,
            issuer: "Reuters Media",
            timestamp: "2024-09-20T10:30:00Z",
            claims: [
              "Content authenticity verified",
              "No AI-generated content detected",
              "Original source confirmed"
            ]
          },
          preliminaryAnalysis: {
            credibilityScore: Math.floor(Math.random() * 40) + 60,
            riskLevel: ["low", "medium", "high"]?.[Math.floor(Math.random() * 3)],
            flags: [
              "Source verification pending",
              "Cross-reference needed",
              "Fact-check recommended"
            ]?.slice(0, Math.floor(Math.random() * 3) + 1)
          },
          relatedClaims: [
            {
              id: 1,
              title: "Similar claim verified as mostly true",
              verdict: "mostly-true",
              confidence: 85
            },
            {
              id: 2,
              title: "Related topic under investigation",
              verdict: "unverified",
              confidence: 45
            }
          ]
        });
        setLoading(false);
      }, 1500);
    }
  }, [url]);

  if (!url) return null;

  const getRiskColor = (level) => {
    const colors = {
      'low': 'text-success bg-success/10 border-success/20',
      'medium': 'text-warning bg-warning/10 border-warning/20',
      'high': 'text-destructive bg-destructive/10 border-destructive/20'
    };
    return colors?.[level] || colors?.medium;
  };

  const getVerdictColor = (verdict) => {
    const colors = {
      'true': 'text-success',
      'mostly-true': 'text-success',
      'mixed': 'text-warning',
      'mostly-false': 'text-destructive',
      'false': 'text-destructive',
      'unverified': 'text-muted-foreground'
    };
    return colors?.[verdict] || colors?.unverified;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevated max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="Shield" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Instant Provenance</h3>
              <p className="text-sm text-muted-foreground">Content verification analysis</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="text-muted-foreground">Analyzing content...</span>
              </div>
            </div>
          ) : analysis ? (
            <div className="space-y-6">
              {/* URL Info */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Link" size={20} className="text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {analysis?.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analysis?.domain} • {analysis?.publishDate} • {analysis?.author}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 break-all">
                      {analysis?.url}
                    </p>
                  </div>
                </div>
              </div>

              {/* C2PA Manifest */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground flex items-center space-x-2">
                  <Icon name="Shield" size={16} />
                  <span>C2PA Content Credentials</span>
                </h4>
                
                {analysis?.c2paManifest?.present ? (
                  <div className="p-4 border border-border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name={analysis?.c2paManifest?.verified ? "CheckCircle" : "AlertCircle"} 
                          size={16} 
                          className={analysis?.c2paManifest?.verified ? "text-success" : "text-warning"}
                        />
                        <span className="text-sm font-medium text-foreground">
                          {analysis?.c2paManifest?.verified ? "Verified" : "Unverified"} Manifest
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {analysis?.c2paManifest?.issuer}
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      {analysis?.c2paManifest?.claims?.map((claim, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Icon name="Check" size={14} className="text-success" />
                          <span className="text-xs text-foreground">{claim}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border border-border rounded-lg">
                    <div className="flex items-center space-x-2 text-muted-foreground">
                      <Icon name="AlertCircle" size={16} />
                      <span className="text-sm">No C2PA manifest found</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Preliminary Analysis */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground flex items-center space-x-2">
                  <Icon name="BarChart3" size={16} />
                  <span>Preliminary Analysis</span>
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border border-border rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Credibility Score</div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            analysis?.preliminaryAnalysis?.credibilityScore >= 80 ? 'bg-success' :
                            analysis?.preliminaryAnalysis?.credibilityScore >= 60 ? 'bg-warning' : 'bg-destructive'
                          }`}
                          style={{ width: `${analysis?.preliminaryAnalysis?.credibilityScore}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {analysis?.preliminaryAnalysis?.credibilityScore}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-3 border border-border rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Risk Level</div>
                    <span className={`text-sm font-medium px-2 py-1 rounded-full border ${getRiskColor(analysis?.preliminaryAnalysis?.riskLevel)}`}>
                      {analysis?.preliminaryAnalysis?.riskLevel?.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  {analysis?.preliminaryAnalysis?.flags?.map((flag, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <Icon name="AlertTriangle" size={14} className="text-warning" />
                      <span className="text-foreground">{flag}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Related Claims */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground flex items-center space-x-2">
                  <Icon name="GitBranch" size={16} />
                  <span>Related Claims</span>
                </h4>
                
                <div className="space-y-2">
                  {analysis?.relatedClaims?.map((claim) => (
                    <div key={claim?.id} className="p-3 border border-border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
                      <div className="flex items-start justify-between">
                        <p className="text-sm text-foreground flex-1">{claim?.title}</p>
                        <div className="flex items-center space-x-2 ml-3">
                          <span className={`text-xs ${getVerdictColor(claim?.verdict)}`}>
                            {claim?.verdict?.replace('-', ' ')}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {claim?.confidence}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-4 border-t border-border">
                <Button
                  variant="default"
                  iconName="Search"
                  iconPosition="left"
                  onClick={() => {
                    // Navigate to full analysis
                    onClose();
                  }}
                >
                  Full Analysis
                </Button>
                <Button
                  variant="outline"
                  iconName="BookOpen"
                  iconPosition="left"
                >
                  Learn More
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Unable to analyze content</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProvenancePane;